import React from "react";
import Button from "./Button";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { Link } from "gatsby";

export type BlogCardProps = {
  /** Title of the post */
  title: string;
  /** Date of the post */
  date: Date;
  /** Source of the image of the blogpost */
  image: string | IGatsbyImageData;
  /** Preview text of the blogpost */
  preview: string;
  /** Blogpost Url */
  postURL: string;
  locale: string;
  /** GenericText.viewAllBlogPosts */
  viewAllTitle?: string;
  /** GenericText.bigBlogCardReadMore */
  readMore?: string;
};

const DateOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
} as const;

export const SmallBlogCard = ({
  title,
  date,
  image,
  preview,
  postURL,
  locale,
  viewAllTitle,
}: BlogCardProps) => {
  return (
    <div
      key={title}
      className="flex max-w-sm flex-col overflow-hidden rounded-lg shadow-lg"
    >
      <div className="-mb-2 flex-shrink-0">
        {typeof image === "string" ? (
          <img className="h-48 w-full object-cover" src={image} alt={title} />
        ) : (
          <GatsbyImage
            image={image}
            className="h-48 w-full object-cover"
            alt={title}
          />
        )}
      </div>
      <div className="flex flex-1 flex-col justify-between bg-accuRankerPurple-500 p-6">
        <div className="flex-1 text-left">
          <time
            className="font-light text-gray-400"
            dateTime={date.toUTCString()}
          >
            {date.toLocaleString(locale, DateOptions)}
          </time>
          <Link to={`/${locale}/blog/${postURL}/`} className={"mt-2 block"}>
            <p className="my-0 text-xl font-medium text-accuRankerOrange-500">
              {title}
            </p>
            <p className="mt-3 text-base font-light text-gray-200">
              {preview}&hellip;
            </p>
          </Link>
        </div>
        {viewAllTitle && (
          <div className="mt-6 flex flex-row items-center">
            <p className="my-auto text-sm font-medium text-gray-900">
              <Link
                to={`/${locale}/blog/`}
                className="text-accuRankerOrange-500 hover:underline"
              >
                {viewAllTitle}
              </Link>
            </p>

            {/*<Button color="orange" className="text-sm ml-auto">*/}
            {/*  <Link to={`/blog/${postURL}`}>Read More</Link>*/}
            {/*</Button>*/}
          </div>
        )}
      </div>
    </div>
  );
};

export const BigBlogCard = ({
  locale,
  title,
  date,
  image,
  preview,
  postURL,
  readMore,
  viewAllTitle,
}: BlogCardProps) => {
  return (
    <div
      key={title}
      className="flex flex-row overflow-hidden rounded-lg shadow-lg"
      style={{ maxWidth: "900px" }}
    >
      <div className="flex flex-1 flex-col justify-between bg-accuRankerPurple-500 p-6">
        <div className="flex-1">
          <time
            className="font-light text-gray-400"
            dateTime={date.toUTCString()}
          >
            {date.toLocaleString(locale, DateOptions)}
          </time>
          <Link to={`/${locale}/blog/` + postURL + "/"} className="mt-2 block">
            <p className="my-0 text-xl font-semibold text-accuRankerOrange-500">
              {title}
            </p>
            <p className="mt-3 text-base font-light text-gray-200">{preview}</p>
          </Link>
        </div>
        <div className="mt-6 flex flex-row items-center">
          {viewAllTitle && (
            <p className="text-sm font-medium text-gray-900">
              <Link
                to={`/${locale}/blog/` + postURL + "/"}
                className="text-accuRankerOrange-500 hover:underline"
              >
                {viewAllTitle}
              </Link>
            </p>
          )}
          {readMore && <Button className="ml-auto text-sm ">{readMore}</Button>}
        </div>
      </div>
      <div
        className="triangle-box-left hidden flex-shrink-0 md:block"
        style={{
          width: "300px",
          background: `transparent url(${image}) no-repeat`,
        }}
      ></div>
    </div>
  );
};

export const SmallTransparentBlogCard = ({
  title,
  date,
  image,
  postURL,
  locale,
  blogSlug,
}: Omit<BlogCardProps, "preview" | "image"> & {
  image: string | IGatsbyImageData;
  blogSlug: string;
}) => {
  return (
    <Link
      key={title}
      className="flex max-w-xs flex-1 cursor-pointer flex-col overflow-hidden"
      to={
        locale === "en"
          ? `/${blogSlug}/${postURL}/`
          : `/${locale}/${blogSlug}/${postURL}/`
      }
    >
      <div className="flex-shrink-0">
        {typeof image === "string" ? (
          <img
            className="h-48 w-full rounded-t-xl rounded-br-xl object-cover shadow-md"
            src={image}
            alt={title}
          />
        ) : (
          <GatsbyImage
            image={image}
            className="h-48 w-full rounded-t-xl rounded-br-xl object-cover shadow-md"
            alt={title}
          />
        )}
      </div>
      <div className="mt-4 flex flex-1 flex-col justify-between">
        <div className="flex-1">
          <time
            className="text-sm font-light text-gray-400"
            dateTime={date.toUTCString()}
          >
            {date.toLocaleString(locale || "en-US", DateOptions)}
          </time>
          <p className="my-2 text-base font-semibold text-accuRankerPurple-500">
            {title}
          </p>
        </div>
      </div>
    </Link>
  );
};
